import React from 'react';

import {
  Badge,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';

import {
  EmceeSheetContestantDTO,
  EventJudgeAgeGroupDTO,
  EventJudgeDivisionDTO,
} from 'API/Generated/data-contracts';
import CsLineIcons from 'cs-line-icons/CsLineIcons';

// =====================================================
// TYPES
// =====================================================

type EmceeSheetsModalProps = {
  show: boolean;

  onHide: () => void;

  divisions: EventJudgeDivisionDTO[];

  loadEmceeSheets: (
    divisionId?: number,
    ageGroupId?: number,
  ) => Promise<EmceeSheetContestantDTO[] | null>;

};

type EmceeAgeGroupSection = {
  divisionId: number;

  divisionName: string;

  ageGroupId?: number;

  ageGroupName: string;

  contestants: EmceeSheetContestantDTO[];
};

type EmceePage =
  | {
      type: 'overview';

      section: EmceeAgeGroupSection;
    }
  | {
      type: 'contestant';

      section: EmceeAgeGroupSection;

      contestant: EmceeSheetContestantDTO;

      contestantIndex: number;
    };

type SelectionStep =
  | 'division'
  | 'ageGroup'
  | 'viewer';

// =====================================================
// COMPONENT
// =====================================================

const EmceeSheetsModal = ({
  show,
  onHide,
  divisions,
  loadEmceeSheets,
}: EmceeSheetsModalProps) => {
  // =====================================================
  // STATE
  // =====================================================

  const [
    selectedDivision,
    setSelectedDivision,
  ] =
    React.useState<EventJudgeDivisionDTO | null>(
      null,
    );

  const [
    selectionStep,
    setSelectionStep,
  ] =
    React.useState<SelectionStep>(
      'division',
    );

  const [
    sections,
    setSections,
  ] =
    React.useState<EmceeAgeGroupSection[]>(
      [],
    );

  const [
    pages,
    setPages,
  ] =
    React.useState<EmceePage[]>(
      [],
    );

  const [
    currentPageIndex,
    setCurrentPageIndex,
  ] =
    React.useState<number>(
      0,
    );

  const [
    loading,
    setLoading,
  ] =
    React.useState<boolean>(
      false,
    );

  const [
    error,
    setError,
  ] =
    React.useState<string | null>(
      null,
    );

const getSideAwardStatus = (
  section:
    EmceeAgeGroupSection,
): {
  isComplete:
    boolean | null;

  completedCount:
    number | null;

  totalCount:
    number | null;
} => {
  const division =
    divisions.find(
      item =>
        Number(
          item.divisionId,
        ) ===
        Number(
          section.divisionId,
        ),
    );

  if (!division) {
    return {
      isComplete: null,
      completedCount: null,
      totalCount: null,
    };
  }

  // =============================================
  // AGE GROUP
  // =============================================

  if (
    section.ageGroupId != null
  ) {
    const ageGroup =
      division.ageGroups?.find(
        item =>
          Number(
            item.ageGroupId,
          ) ===
          Number(
            section.ageGroupId,
          ),
      );

    return {
      isComplete:
        ageGroup
          ?.sideAwardsComplete ??
        null,

      completedCount:
        ageGroup
          ?.sideAwardsCompletedCount ??
        null,

      totalCount:
        ageGroup
          ?.sideAwardsTotalCount ??
        null,
    };
  }

  // =============================================
  // DIVISION WITHOUT AGE GROUPS
  // =============================================

  return {
    isComplete:
      division
        .sideAwardsComplete ??
      null,

    completedCount:
      division
        .sideAwardsCompletedCount ??
      null,

    totalCount:
      division
        .sideAwardsTotalCount ??
      null,
  };
};

  // =====================================================
  // SWIPE REFS
  // =====================================================
const goToContestant =
  (
    section:
      EmceeAgeGroupSection,
    contestant:
      EmceeSheetContestantDTO,
  ): void => {
    const contestantId =
      contestant
        .registeredContestantId;

    const pageIndex =
      pages.findIndex(
        page =>
          page.type ===
            'contestant' &&
          page.section
            .divisionId ===
            section.divisionId &&
          page.section
            .ageGroupId ===
            section.ageGroupId &&
          (
            contestantId != null
              ? page.contestant
                  .registeredContestantId ===
                contestantId
              : page.contestant
                  .contestantNumber ===
                contestant
                  .contestantNumber
          ),
      );

    if (
      pageIndex < 0
    ) {
      console.warn(
        '[EmceeSheets] Contestant page not found.',
        {
          contestant,
          section,
        },
      );

      return;
    }

    setCurrentPageIndex(
      pageIndex,
    );

    setSelectionStep(
      'viewer',
    );
  };
  
  const touchStartXRef =
    React.useRef<number | null>(
      null,
    );

  const touchEndXRef =
    React.useRef<number | null>(
      null,
    );

  // =====================================================
  // CURRENT DATA
  // =====================================================

  const selectedDivisionAgeGroups =
    selectedDivision
      ?.ageGroups ??
    [];

  const currentPage =
    pages[
      currentPageIndex
    ];

  const currentSection =
    currentPage
      ?.section ??
    null;

  // =====================================================
  // HELPERS
  // =====================================================

  const sortContestants = (
    contestants:
      | EmceeSheetContestantDTO[]
      | null
      | undefined,
  ): EmceeSheetContestantDTO[] => {
    return [
      ...(contestants ?? []),
    ].sort(
      (a, b) => {
        const aNumber =
          Number(
            a.contestantNumber,
          );

        const bNumber =
          Number(
            b.contestantNumber,
          );

        const aValid =
          Number.isFinite(
            aNumber,
          );

        const bValid =
          Number.isFinite(
            bNumber,
          );

        if (
          aValid &&
          bValid
        ) {
          return (
            aNumber -
            bNumber
          );
        }

        return String(
          a.contestantNumber ??
            '',
        ).localeCompare(
          String(
            b.contestantNumber ??
              '',
          ),
        );
      },
    );
  };

  const formatBirthday = (
    birthday?:
      | string
      | null,
  ): string => {
    if (!birthday) {
      return '—';
    }

    const date =
      new Date(
        birthday,
      );

    if (
      Number.isNaN(
        date.getTime(),
      )
    ) {
      return birthday;
    }

    return date.toLocaleDateString(
      'en-US',
      {
        month:
          'long',

        day:
          'numeric',

        year:
          'numeric',
      },
    );
  };

  const resetViewer =
    (): void => {
      setSections(
        [],
      );

      setPages(
        [],
      );

      setCurrentPageIndex(
        0,
      );

      setError(
        null,
      );
    };

  // =====================================================
  // BUILD PAGES
  //
  // Each age group gets:
  //
  // OVERVIEW
  // Contestant
  // Contestant
  // Contestant
  //
  // then the next age group overview.
  // =====================================================

  const buildPages = (
    nextSections:
      EmceeAgeGroupSection[],
  ): EmceePage[] => {
    return nextSections.flatMap(
      (section) => {
        const contestantPages:
          EmceePage[] =
          section.contestants.map(
            (
              contestant,
              contestantIndex,
            ) => ({
              type:
                'contestant',

              section,

              contestant,

              contestantIndex,
            }),
          );

        return [
          {
            type:
              'overview',

            section,
          } as EmceePage,

          ...contestantPages,
        ];
      },
    );
  };

  // =====================================================
  // RESET WHEN MODAL CLOSES
  // =====================================================

  React.useEffect(() => {
    if (show) {
      return;
    }

    setSelectedDivision(
      null,
    );

    setSelectionStep(
      'division',
    );

    resetViewer();
  }, [show]);

  // =====================================================
  // DIVISION SELECTION
  // =====================================================

  const chooseDivision = (
    division:
      EventJudgeDivisionDTO,
  ): void => {
    setSelectedDivision(
      division,
    );

    resetViewer();

    const ageGroups =
      division.ageGroups ??
      [];

    if (
      ageGroups.length >
      0
    ) {
      setSelectionStep(
        'ageGroup',
      );

      return;
    }

    void loadDivisionWithoutAgeGroups(
      division,
    );
  };

  // =====================================================
  // DIVISION WITHOUT AGE GROUPS
  // =====================================================

const loadDivisionWithoutAgeGroups =
  async (
    division:
      EventJudgeDivisionDTO,
  ): Promise<void> => {
    const divisionId =
      division.divisionId;

    if (!divisionId) {
      console.error(
        'Cannot load emcee sheets: division is missing divisionId.',
      );

      setError(
        'Unable to load emcee sheets.',
      );

      return;
    }

    setLoading(
      true,
    );

    setError(
      null,
    );

    try {
      const contestants =
        await loadEmceeSheets(
          divisionId,
          undefined,
        );

      const section:
        EmceeAgeGroupSection =
        {
          divisionId,

          divisionName:
            division.divisionName ??
            'Division',

          ageGroupName:
            '',

          contestants:
            sortContestants(
              contestants,
            ),
        };

      const nextSections =
        [
          section,
        ];

      setSections(
        nextSections,
      );

      setPages(
        buildPages(
          nextSections,
        ),
      );

      setCurrentPageIndex(
        0,
      );

      setSelectionStep(
        'viewer',
      );
    } catch (err) {
      console.error(
        'Failed to load emcee sheets:',
        err,
      );

      setError(
        'Unable to load emcee sheets.',
      );
    } finally {
      setLoading(
        false,
      );
    }
  };

  // =====================================================
  // AGE GROUP SELECTION
  //
  // Load selected group AND remaining groups
  // in this division so the emcee can continue
  // straight through.
  // =====================================================

  const chooseAgeGroup =
    async (
      ageGroup:
        EventJudgeAgeGroupDTO,
    ): Promise<void> => {
      if (
        !selectedDivision
      ) {
        return;
      }

      if (!selectedDivision.divisionId) {
  console.error(
    'Cannot load emcee sheets: selected division is missing divisionId.',
  );

  return;
}

      setLoading(
        true,
      );

      setError(
        null,
      );

      try {
        const ageGroups =
          selectedDivision
            .ageGroups ??
          [];

        const selectedIndex =
          ageGroups.findIndex(
            (group) =>
              group.ageGroupId ===
              ageGroup.ageGroupId,
          );

        const remainingAgeGroups =
          selectedIndex >= 0
            ? ageGroups.slice(
                selectedIndex,
              )
            : [
                ageGroup,
              ];

        const nextSections:
          EmceeAgeGroupSection[] =
          [];

        for (
          const group
          of remainingAgeGroups
        ) {
          const contestants =
            await loadEmceeSheets(
              selectedDivision
                .divisionId,
              group.ageGroupId,
            );

          nextSections.push(
            {
              divisionId:
                selectedDivision
                  .divisionId,

              divisionName:
                selectedDivision
                  .divisionName ??
                'Division',

              ageGroupId:
                group.ageGroupId,

              ageGroupName:
                group.ageGroupName ??
                'Age Group',

              contestants:
                sortContestants(
                  contestants,
                ),
            },
          );
        }

        setSections(
          nextSections,
        );

        setPages(
          buildPages(
            nextSections,
          ),
        );

        setCurrentPageIndex(
          0,
        );

        setSelectionStep(
          'viewer',
        );
      } catch (err) {
        console.error(
          'Failed to load emcee sheets:',
          err,
        );

        setError(
          'Unable to load emcee sheets.',
        );
      } finally {
        setLoading(
          false,
        );
      }
    };

  // =====================================================
  // HOME
  //
  // Returns all the way to Division selection.
  // =====================================================

  const goToHome =
    (): void => {
      setSelectedDivision(
        null,
      );

      resetViewer();

      setSelectionStep(
        'division',
      );
    };

  // =====================================================
  // NAVIGATION HELPERS
  // =====================================================

  const hasPrevious =
    currentPageIndex >
    0;

  const hasNext =
    currentPageIndex <
    pages.length -
      1;

  const getCurrentSectionIndex =
    (): number => {
      if (
        !currentSection
      ) {
        return -1;
      }

      return sections.findIndex(
        (section) => {
          if (
            currentSection
              .ageGroupId != null
          ) {
            return (
              section.divisionId ===
                currentSection
                  .divisionId &&
              section.ageGroupId ===
                currentSection
                  .ageGroupId
            );
          }

          return (
            section.divisionId ===
            currentSection
              .divisionId
          );
        },
      );
    };

  const getNextSection =
    ():
      | EmceeAgeGroupSection
      | null => {
      const currentSectionIndex =
        getCurrentSectionIndex();

      if (
        currentSectionIndex <
        0
      ) {
        return null;
      }

      return (
        sections[
          currentSectionIndex +
            1
        ] ??
        null
      );
    };

  const isLastSection =
    (): boolean => {
      const sectionIndex =
        getCurrentSectionIndex();

      return (
        sectionIndex >=
          0 &&
        sectionIndex ===
          sections.length -
            1
      );
    };

  const isLastContestantInSection =
    (): boolean => {
      if (
        !currentPage ||
        currentPage.type !==
          'contestant'
      ) {
        return false;
      }

      return (
        currentPage
          .contestantIndex ===
        currentPage.section
          .contestants.length -
          1
      );
    };

  // =====================================================
  // PREVIOUS
  // =====================================================

  const goPrevious =
    (): void => {
      if (
        !hasPrevious
      ) {
        return;
      }

      setCurrentPageIndex(
        (current) =>
          current - 1,
      );
    };

  // =====================================================
  // NEXT
  // =====================================================

  const goNext =
    (): void => {
      if (
        !currentPage
      ) {
        return;
      }

      // Last contestant in the final
      // age group of this division.
      if (
        currentPage.type ===
          'contestant' &&
        isLastContestantInSection() &&
        isLastSection()
      ) {
        goToHome();

        return;
      }

      if (!hasNext) {
        goToHome();

        return;
      }

      setCurrentPageIndex(
        (current) =>
          current + 1,
      );
    };

  // =====================================================
  // NEXT BUTTON LABEL
  // =====================================================

  const getNextButtonLabel =
    (): string => {
      if (
        !currentPage
      ) {
        return 'Next →';
      }

      // Age group home page
      if (
        currentPage.type ===
        'overview'
      ) {
        return 'BEGIN →';
      }

      // Last contestant in current group
      if (
        isLastContestantInSection()
      ) {
        const nextSection =
          getNextSection();

        if (nextSection) {
          return `Go to ${nextSection.ageGroupName} →`;
        }

        return 'To Next Division →';
      }

      return 'Next →';
    };

  // =====================================================
  // FOOTER PROGRESS
  // =====================================================

  const getFooterProgress =
    (): string => {
      if (
        !currentPage
      ) {
        return '';
      }

      if (
        currentPage.type ===
        'overview'
      ) {
        const count =
          currentPage.section
            .contestants.length;

        return `${count} ${
          count === 1
            ? 'contestant'
            : 'contestants'
        }`;
      }

      return `Contestant ${
        currentPage.contestantIndex +
        1
      } of ${
        currentPage.section
          .contestants.length
      }`;
    };

  // =====================================================
  // SWIPE
  // =====================================================

  const handleTouchStart = (
    event:
      React.TouchEvent<HTMLDivElement>,
  ): void => {
    touchEndXRef.current =
      null;

    touchStartXRef.current =
      event.targetTouches[0]
        ?.clientX ??
      null;
  };

  const handleTouchMove = (
    event:
      React.TouchEvent<HTMLDivElement>,
  ): void => {
    touchEndXRef.current =
      event.targetTouches[0]
        ?.clientX ??
      null;
  };

  const handleTouchEnd =
    (): void => {
      if (
        touchStartXRef.current ===
          null ||
        touchEndXRef.current ===
          null
      ) {
        return;
      }

      const distance =
        touchStartXRef.current -
        touchEndXRef.current;

      const minimumSwipe =
        50;

      // Swipe left
      if (
        distance >
        minimumSwipe
      ) {
        goNext();
      }

      // Swipe right
      if (
        distance <
        -minimumSwipe
      ) {
        goPrevious();
      }

      touchStartXRef.current =
        null;

      touchEndXRef.current =
        null;
    };

  // =====================================================
  // HEADER
  // =====================================================

  const renderHeader =
    (): React.ReactNode => {
      if (
        selectionStep ===
        'division'
      ) {
        return (
          <Modal.Title className="fs-2 fw-bold mb-0">
            Select Division
          </Modal.Title>
        );
      }

      if (
        selectionStep ===
        'ageGroup'
      ) {
        return (
          <Modal.Title className="fs-2 fw-bold mb-0">
            {selectedDivision
              ?.divisionName ??
              'Division'}

            <span className="text-muted mx-2">
              |
            </span>

            Select Age Group
          </Modal.Title>
        );
      }

      if (
        !currentSection
      ) {
        return null;
      }

const sideAwardStatus =
  getSideAwardStatus(
    currentSection,
  );

return (
  <Modal.Title className="fs-2 fw-bold mb-0">
    <span className="d-flex align-items-center gap-2">
      <span>
        {
          currentSection
            .divisionName
        }

        {currentSection
          .ageGroupName && (
          <>
            <span className="text-muted mx-2">
              |
            </span>

            {
              currentSection
                .ageGroupName
            }
          </>
        )}
      </span>

    </span>
  </Modal.Title>
);
    };

  // =====================================================
  // DIVISION SELECTION
  // =====================================================

  const renderDivisionSelection =
    (): React.ReactNode => {
      return (
        <div
          style={{
            maxWidth:
              '1200px',

            margin:
              '0 auto',
          }}
        >
          <div className="text-center mb-4">
            <h2 className="fw-bold mb-1">
              Select a Division
            </h2>

            <div className="fs-5 text-muted">
              Choose the division
              the emcee is working
              with.
            </div>
          </div>

          <Row className="g-3">
            {divisions.map(
              (division) => {
                const ageGroups =
                  division
                    .ageGroups ??
                  [];

                const ageGroupCount =
                  ageGroups.length;

const sideAwardsComplete:
  boolean | null =
  ageGroups.length > 0
    ? ageGroups.every(
        (ageGroup) =>
          ageGroup
            .sideAwardsComplete ===
          true,
      )
      ? true
      : ageGroups.some(
          (ageGroup) =>
            ageGroup
              .sideAwardsComplete !=
            null,
        )
      ? false
      : null
    : division
        .sideAwardsComplete ??
      null;

               const contestantCount =
  ageGroups.length > 0
    ? ageGroups.reduce(
        (
          total,
          ageGroup,
        ) =>
          total +
          (
            ageGroup.contestants
              ?.length ??
            0
          ),
        0,
      )
    : (
        division.contestants
          ?.length ??
        0
      );

                return (
                  <Col
                    key={
                      division.divisionId
                    }
                    xs={12}
                    md={6}
                    lg={4}
                  >
                    <Card
                      role="button"
                      className="h-100 shadow-sm"
                      style={{
                        cursor:
                          'pointer',
                      }}
                      onClick={() => {
                        chooseDivision(
                          division,
                        );
                      }}
                    >
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div>
                            <div className="fw-bold fs-3">
                              {
                                division.divisionName
                              }
                            </div>

                            <div className="fs-5 text-muted mt-1">
                              {
                                contestantCount
                              }{' '}
                              {contestantCount ===
                              1
                                ? 'contestant'
                                : 'contestants'}
                            </div>

                            {ageGroupCount >
                              0 && (
                              <div className="text-muted">
                                {
                                  ageGroupCount
                                }{' '}
                                {ageGroupCount ===
                                1
                                  ? 'age group'
                                  : 'age groups'}
                              </div>
                            )}

{sideAwardsComplete != null && (
  <Badge
    bg={
      sideAwardsComplete
        ? 'success'
        : 'warning'
    }
    text={
      sideAwardsComplete
        ? undefined
        : 'dark'
    }
    className="mt-2 text-small"
  >
    <CsLineIcons
      icon={
        sideAwardsComplete
          ? 'check'
          : 'warning-hexagon'
      }
      className="me-1"
    />

    {sideAwardsComplete
      ? 'Side Awards Complete'
      : 'Side Awards Incomplete'}
  </Badge>
)}
                          </div>

                          <div className="fs-2 text-muted">
                            ›
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              },
            )}
          </Row>
        </div>
      );
    };

  // =====================================================
  // AGE GROUP SELECTION
  // =====================================================

  const renderAgeGroupSelection =
    (): React.ReactNode => {
      return (
        <div
          style={{
            maxWidth:
              '1200px',

            margin:
              '0 auto',
          }}
        >
          <div className="text-center mb-4">
            <h2 className="fw-bold mb-1">
              Select an Age Group
            </h2>

            <div className="fs-5 text-muted">
              {
                selectedDivision
                  ?.divisionName
              }
            </div>
          </div>

          <Row className="g-3">
            {selectedDivisionAgeGroups.map(
              (ageGroup) => {
                const contestantCount =
                  ageGroup
                    .contestants
                    ?.length ??
                  0;

                return (
                  <Col
                    key={
                      ageGroup.ageGroupId
                    }
                    xs={12}
                    md={6}
                    lg={4}
                  >
                    <Card
                      role="button"
                      className="h-100 shadow-sm"
                      style={{
                        cursor:
                          'pointer',
                      }}
                      onClick={() => {
                        void chooseAgeGroup(
                          ageGroup,
                        );
                      }}
                    >
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div>
                            <div className="fw-bold fs-3">
                              {
                                ageGroup.ageGroupName
                              }
                            </div>

                            <div className="fs-5 text-muted mt-1">
                              {
                                contestantCount
                              }{' '}
                              {contestantCount ===
                              1
                                ? 'contestant'
                                : 'contestants'}
                            </div>
{ageGroup
  .sideAwardsComplete !=
  null && (
  <Badge
    bg={
      ageGroup
        .sideAwardsComplete
        ? 'success'
        : 'warning'
    }
    text={
      ageGroup
        .sideAwardsComplete
        ? undefined
        : 'dark'
    }
    className="mt-2 text-small"
  >
    <CsLineIcons
      icon={
        ageGroup
          .sideAwardsComplete
          ? 'check'
          : 'warning-hexagon'
      }
      className="me-1"
    />

    {ageGroup
      .sideAwardsComplete
      ? 'Side Awards Complete'
      : 'Side Awards Incomplete'}
  </Badge>
)}
                          </div>

                          <div className="fs-2 text-muted">
                            ›
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              },
            )}
          </Row>
        </div>
      );
    };

  // =====================================================
  // AGE GROUP OVERVIEW
  // =====================================================

  const renderOverview = (
    section:
      EmceeAgeGroupSection,
  ): React.ReactNode => {
    const count =
      section.contestants
        .length;

const sideAwardStatus =
  getSideAwardStatus(
    currentSection,
  );

    return (
      <div
        style={{
          maxWidth:
            '850px',

          margin:
            '0 auto',
        }}
      >
        {/* ============================================= */}
{/* SIDE AWARD STATUS */}
{/* ============================================= */}

<div className="text-center mb-4">
 {sideAwardStatus
  .isComplete != null && (
  <div className="text-center mb-4">
    {sideAwardStatus
      .isComplete === true ? (
      <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded bg-success text-white fw-bold">
        <CsLineIcons
          icon="check"
        />

        Side Awards Complete
      </div>
    ) : (
      <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded bg-warning text-dark fw-bold">
        <CsLineIcons
          icon="warning-hexagon"
        />

        Side Awards Incomplete

        {sideAwardStatus
          .totalCount != null &&
          sideAwardStatus
            .completedCount != null && (
            <span>
              {' '}
              (
              {
                sideAwardStatus
                  .completedCount
              }
              /
              {
                sideAwardStatus
                  .totalCount
              }
              )
            </span>
          )}
      </div>
    )}
  </div>
)}
</div>

        {/* COUNT */}

        <div className="text-center mb-4">
          <div className="display-4 fw-bold text-primary">
            {count}
          </div>

          <div className="fs-4 fw-semibold">
            {count === 1
              ? 'Contestant'
              : 'Contestants'}
          </div>
        </div>

        {/* CONTESTANT LIST */}

        {count >
        0 ? (
          <div className="border rounded overflow-hidden bg-background">
            {section.contestants.map(
              (
                contestant,
                index,
              ) => (
             <div
  key={
    contestant.registeredContestantId ??
    index
  }
  role="button"
  tabIndex={0}
  className={`d-flex align-items-center justify-content-between gap-3 px-4 py-3 ${
    index <
    count -
      1
      ? 'border-bottom'
      : ''
  }`}
  style={{
    cursor:
      'pointer',
  }}
  onClick={() => {
    goToContestant(
      section,
      contestant,
    );
  }}
  onKeyDown={event => {
    if (
      event.key ===
        'Enter' ||
      event.key ===
        ' '
    ) {
      event.preventDefault();

      goToContestant(
        section,
        contestant,
      );
    }
  }}
>
  <div className="d-flex align-items-center gap-3">
    <div
      className="fw-bold fs-3 text-primary"
      style={{
        minWidth:
          '90px',
      }}
    >
      #
      {
        contestant
          .contestantNumber
      }
    </div>

    <div className="fw-bold fs-4">
      {
        contestant
          .contestantName
      }
    </div>
  </div>

  <div className="fs-2 text-muted">
    ›
  </div>
</div>
              ),
            )}
          </div>
        ) : (
          <Card>
            <Card.Body className="text-center text-muted py-5 fs-5">
              No contestants in
              this age group.
            </Card.Body>
          </Card>
        )}
      </div>
    );
  };

  // =====================================================
  // CONTESTANT SHEET
  // =====================================================

  const renderContestant = (
    page: Extract<
      EmceePage,
      {
        type:
          'contestant';
      }
    >,
  ): React.ReactNode => {
    const contestant =
      page.contestant;

    return (
      <div
        style={{
          maxWidth:
            '1000px',

          margin:
            '0 auto',
        }}
      >
        {/* ============================================= */}
        {/* CONTESTANT NAME / NUMBER */}
        {/* ============================================= */}

        <div className="text-center mb-4">
          <div
            className="fw-bold text-primary lh-1"
            style={{
              fontSize:
                'clamp(2.75rem, 6vw, 4.5rem)',
            }}
          >
            #
            {
              contestant.contestantNumber
            }
          </div>

          <div
            className="fw-bold mt-2"
            style={{
              fontSize:
                'clamp(2.5rem, 6vw, 4.5rem)',

              lineHeight:
                1.05,
            }}
          >
            {
              contestant.contestantName
            }
          </div>
        </div>

        {/* ============================================= */}
        {/* QUICK DETAILS */}
        {/* ============================================= */}

        <Row className="g-2 mb-3">
          {/* BIRTHDAY */}

          <Col
            xs={6}
            md={3}
          >
            <Card className="h-100 border-0 bg-light">
              <Card.Body className="p-3 text-center">
                <div className="small text-muted fw-bold text-uppercase">
                  Birthday
                </div>

                <div className="fw-bold fs-5 mt-1 text-body">
                  {formatBirthday(
                    contestant.birthday,
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* AGE */}

          <Col
            xs={6}
            md={3}
          >
            <Card className="h-100 border-0 bg-light">
              <Card.Body className="p-3 text-center">
                <div className="small text-muted fw-bold text-uppercase">
                  Age
                </div>

                <div className="fw-bold fs-4 mt-1  text-body">
                  {contestant.age ??
                    '—'}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* HAIR */}

          <Col
            xs={6}
            md={3}
          >
            <Card className="h-100 border-0 bg-light">
              <Card.Body className="p-3 text-center">
                <div className="small text-muted fw-bold text-uppercase">
                  Hair
                </div>

                <div className="fw-bold fs-4 mt-1 text-body">
                  {contestant.hairColor ||
                    '—'}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* EYES */}

          <Col
            xs={6}
            md={3}
          >
            <Card className="h-100 border-0 bg-light">
              <Card.Body className="p-3 text-center">
                <div className="small text-muted fw-bold text-uppercase">
                  Eyes
                </div>

                <div className="fw-bold fs-4 mt-1 text-body">
                  {contestant.eyeColor ||
                    '—'}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* ============================================= */}
        {/* HOBBIES */}
        {/* ============================================= */}

        <Card className="border-primary">
          <Card.Body className="p-3 p-md-4">
            <div className="small text-muted fw-bold text-uppercase mb-1">
              Hobbies
            </div>

            <div
              className="fw-semibold"
              style={{
                fontSize:
                  'clamp(1.25rem, 2.5vw, 1.6rem)',

                lineHeight:
                  1.4,
              }}
            >
              {contestant.hobbies ||
                '—'}
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <Modal
      show={
        show
      }
      onHide={
        onHide
      }
      fullscreen
    >
      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <Modal.Header
        closeButton
        className="py-3 px-4"
      >
        {renderHeader()}
      </Modal.Header>

      {/* ================================================= */}
      {/* BODY */}
      {/* ================================================= */}

      <Modal.Body
        className="p-3 p-md-4"
        onTouchStart={
          selectionStep ===
          'viewer'
            ? handleTouchStart
            : undefined
        }
        onTouchMove={
          selectionStep ===
          'viewer'
            ? handleTouchMove
            : undefined
        }
        onTouchEnd={
          selectionStep ===
          'viewer'
            ? handleTouchEnd
            : undefined
        }
      >
        {/* ============================================= */}
        {/* LOADING */}
        {/* ============================================= */}

        {loading && (
          <div className="text-center py-5">
            <Spinner
              animation="border"
            />

            <div className="fs-5 mt-3">
              Loading Emcee
              Sheets...
            </div>
          </div>
        )}

        {/* ============================================= */}
        {/* ERROR */}
        {/* ============================================= */}

        {error &&
          !loading && (
            <div className="alert alert-danger">
              {error}
            </div>
          )}

        {/* ============================================= */}
        {/* SELECT DIVISION */}
        {/* ============================================= */}

        {!loading &&
          selectionStep ===
            'division' &&
          renderDivisionSelection()}

        {/* ============================================= */}
        {/* SELECT AGE GROUP */}
        {/* ============================================= */}

        {!loading &&
          selectionStep ===
            'ageGroup' &&
          renderAgeGroupSelection()}

        {/* ============================================= */}
        {/* EMCEE VIEWER */}
        {/* ============================================= */}

        {!loading &&
          selectionStep ===
            'viewer' &&
          currentPage && (
            <>
              {currentPage.type ===
              'overview'
                ? renderOverview(
                    currentPage.section,
                  )
                : renderContestant(
                    currentPage,
                  )}
            </>
          )}
      </Modal.Body>

      {/* ================================================= */}
      {/* FOOTER */}
      {/* ================================================= */}

      {selectionStep ===
        'viewer' &&
        currentPage && (
     <Modal.Footer className="px-3 px-md-4 py-3">
  <div className="w-100">
    {/* ========================================= */}
    {/* PROGRESS ROW */}
    {/* ========================================= */}

    <Row className="mb-3">
      <Col xs={12} className="text-center">
        <div className="fw-bold fs-5">
          {getFooterProgress()}
        </div>

        <div className="small text-muted d-none d-md-block">
          Swipe left or right
        </div>
      </Col>
    </Row>

    {/* ========================================= */}
    {/* NAVIGATION ROW */}
    {/* ========================================= */}

    <Row className="align-items-center g-2">
      {/* ======================================= */}
      {/* PREVIOUS + HOME */}
      {/* ======================================= */}

      <Col xs={6}>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="lg"
            disabled={!hasPrevious}
            onClick={goPrevious}
            aria-label="Previous contestant"
            title="Previous"
          >
            <CsLineIcons
              icon="chevron-left"
            />
          </Button>

          <Button
            variant="outline-secondary"
            size="lg"
            onClick={goToHome}
            aria-label="Emcee Sheets Home"
            title="Choose Division or Age Group"
          >
            <CsLineIcons
              icon="home"
            />
          </Button>
        </div>
      </Col>

      {/* ======================================= */}
      {/* NEXT */}
      {/* ======================================= */}

      <Col
        xs={6}
        className="text-end"
      >
        <Button
          variant="primary"
          size="lg"
          className="fw-bold"
          onClick={goNext}
        >
          {getNextButtonLabel()}

          <CsLineIcons
            icon="chevron-right"
            className="ms-2"
          />
        </Button>
      </Col>
    </Row>
  </div>
</Modal.Footer>
      )}
    </Modal>
  );
};

export default EmceeSheetsModal;